import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableCell,
  Button,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  findStrategy,
  TABLE_HEAD_FUND_SWAP_REBALANCE_DATA,
} from "../../../../../../component/Methods";
import SwapRebalancedData from "../../RebalancingInfo/FundRebalance/Step1/components/SwapRebalancedData";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../../../Redux/Reducers/uiSlice";
import { useDispatch, useSelector } from "react-redux";
import SwapRebalanceInfoSkeleton from "../../../../Skeletons/SwapRebalanceInfoSkeleton";
import { sendWebSocketMessage } from "../../../../../../hooks/useWebSocket";
import {selectActiveRequests } from "../../../../../../Redux/Reducers/selectors";
import { useRebalancerState } from "../../../../../../hooks/useOptimizedState";

const SwapRebalanceInfo = ({
  setExistingNewArray = () => {},
  existingNewArray,
  contracts,
  setCurrentStep,
  setExecutionStyles,
}) => {
  const theme = useTheme();
  const { swapStrategiesAvailableDate, strategies } = useRebalancerState();
  const activeRequests = useSelector(selectActiveRequests); 
  const dispatch = useDispatch();
  useEffect(() => {
    if (!contracts || !contracts.length) {
      return;
    }

    contracts.forEach((contract) => {
      if (contract.rebalancerItem && contract.rebalancerItem.length > 0) {
        const updatedExistingNewArray = contract.rebalancerItem.map((item) => {
          return {
            ticker: item?.ticker?.stock
              ? `${item?.ticker?.symbol}/${item?.ticker?.stock}`
              : item?.ticker?.symbol,
            position: item?.currentWeight * 100,
            signal: item?.newWeight * 100,
            diff: item?.newWeight * 100 - item?.currentWeight * 100,
            style: "VWAP_OVER_DAY",
            startTime: "3:00",
            endTime: "7:00",
            participation: "10.00",
          };
        });

        setExistingNewArray((prev) => {
          const newState = {
            ...prev,
            [contract.id]: updatedExistingNewArray,
          };
          return newState;
        });
      } else {
      }
    });
  }, [contracts, setExistingNewArray]);

  const getDummySummaryData = (contractId) => {
    if (!existingNewArray || !existingNewArray[contractId]) {
      return [
        {
          name: "Long Positions",
          existing: "0.00",
          new: "0.00",
        },
        {
          name: "Short Positions",
          existing: "0.00",
          new: "0.00",
        },
      ];
    }

    const items = existingNewArray[contractId];

    const existingLongTotal = items
      ?.filter((item) => item?.position > 0)
      .reduce((sum, item) => sum + item?.position, 0);

    const existingShortTotal = items
      ?.filter((item) => item?.position < 0)
      .reduce((sum, item) => sum + item?.position, 0);

    const newLongTotal = items
      ?.filter((item) => item?.signal > 0)
      .reduce((sum, item) => sum + item?.signal, 0);

    const newShortTotal = items
      ?.filter((item) => item?.signal < 0)
      ?.reduce((sum, item) => sum + item?.signal, 0);

    return [
      {
        name: "Long Positions",
        existing: existingLongTotal.toFixed(2),
        new: newLongTotal.toFixed(2),
      },
      {
        name: "Short Positions",
        existing: existingShortTotal.toFixed(2),
        new: newShortTotal.toFixed(2),
      },
    ];
  };

  const onNext = () => {
    if (contracts?.length > 0) {
      const ids = contracts?.map((contract) => contract.id);

      const tickerExecutionStyles = {};
      contracts.forEach((contract) => {
        if (existingNewArray && existingNewArray[contract.id]) {
          const executionStyles = existingNewArray[contract.id].map((item) => ({
            ticker: item.ticker.split("/")[0],
            executionStyle: item.style || "MOC",
            executionParticipationRate:
              parseFloat(item.participation) / 100 || 0.001,
          }));

          tickerExecutionStyles[contract.id] = executionStyles;
        }
      });
      setExecutionStyles(tickerExecutionStyles);
      const data = {
        strategyIds: ids,
        step: 3,
        availableDates: swapStrategiesAvailableDate,
        tickerExecutionStyles: tickerExecutionStyles,
        newParams: {},
      };

      const socketRequest = {
        action: "rebalance_swaps",
        data: data,
      };
      sendWebSocketMessage(JSON.stringify(socketRequest));
      dispatch(addActiveRequests("rebalance_swaps"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Checking Validity of Rebalancer Items"));
    }
    setCurrentStep((prev) => prev + 1);
  };
  const isLoadingRebalancerData = activeRequests.includes("rebalance_swaps");

  if (isLoadingRebalancerData) {
    return <SwapRebalanceInfoSkeleton />;
  }
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
        borderRadius: "4px",
        width: "100%",
        marginRight: 1,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 1,
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "20px !important",
            }}
          >
            Review Data
          </Typography>
        </Box>

        {contracts?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={onNext}
              sx={{ width: "150px", height: "40px" }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexWrap: "nowrap",
          overflowX: "auto",
          gap: 2,
          p: 2,
        }}
      >
        {contracts?.map((contract) => {
          return (
            <Box
              key={contract?.id}
              sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: "400px",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "900",
                  fontSize: "18px",
                  marginBottom: 1,
                }}
              >
                {contract?.name}
              </Typography>

              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  borderCollapse: "collapse",
                  border: "1px solid #7C7C7C",
                }}
              >
                <TableBody>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                    <TableCell
                      align="left"
                      colSpan={2}
                      sx={{
                        fontSize: "12px",
                        border: "none",
                        paddingLeft: "12px",
                        fontWeight: "bold",
                        paddingBottom: "10px",
                      }}
                    >
                      Summary
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "12px",
                        border: "none",
                        fontWeight: "bold",
                        paddingBottom: "10px",
                      }}
                    >
                      Existing
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "12px",
                        border: "none",
                        fontWeight: "bold",
                        paddingBottom: "10px",
                      }}
                    >
                      New
                    </TableCell>
                  </TableRow>

                  {getDummySummaryData(contract.id).map((row) => (
                    <TableRow key={row.name}>
                      <TableCell
                        colSpan={2}
                        align="left"
                        sx={{
                          fontSize: "12px",
                          paddingBottom: "2px",
                          paddingLeft: "15px",
                          border: "none",
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "12px",
                          border: "none",
                          paddingBottom: "2px",
                        }}
                      >
                        {row.existing}%
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "12px",
                          border: "none",
                          paddingBottom: "2px",
                        }}
                      >
                        {row.new}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {existingNewArray && existingNewArray[contract.id] ? (
                <SwapRebalancedData
                  existingNewArray={existingNewArray[contract.id]}
                  tableHeader={TABLE_HEAD_FUND_SWAP_REBALANCE_DATA}
                  setExistingNewArray={(updatedArray) => {
                    setExistingNewArray((prev) => ({
                      ...prev,
                      [contract.id]: updatedArray,
                    }));
                  }}
                />
              ) : (
                <p>
                  No data available for contract{" "}
                  {findStrategy(contract.id, strategies)}
                </p>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SwapRebalanceInfo;
